import { AsyncPipe } from '@angular/common';
import {
  Component,
  effect,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  GcButtonComponent,
  GcLoadingSpinnerComponent,
  GcSkeletonComponent,
  GcTableComponent,
  GcTextComponent,
  IpoTranslatePipe,
} from '@ip-only/front-end-library';
import { Observable, Subject, takeUntil } from 'rxjs';
import { ErrorInformationComponent } from '../../shared/error-information/error-information.component';
import { FeedbackAlertComponent } from '../../shared/feedback-alert/feedback-alert.component';
import { isTopLevelDomainFI } from '../../shared/util/deploy-helper';
import { ScreenSizeClassComponent } from '../../shared/util/screen-size/screen-size.component';
import { ResourcesService } from '../../user/resources.service';
import { ConstructionDocumentsWrapperComponent } from '../construction-documents-wrapper/construction-documents-wrapper.component';
import {
  ReportTypes,
  SharepointExportItemStatus,
  SharepointExportResponse,
  SharepointExportResponseQueryLoadingState,
} from '../entities/sharepoint-export-entities';
import { TableRowsSharepointExportPipe } from '../pipes/table-rows-sharepoint-export.pipe';
import { SharepointExportTableService } from '../service/sharepoint-export-table.service';
import { SharepointExportService } from '../service/sharepoint-export.service';
import { HelpModalComponent } from './help-modal/help-modal.component';
import { OverwriteReportsConfimationModalComponent } from './overwrite-reports-confimation-modal/overwrite-reports-confimation-modal.component';

@Component({
  selector: 'app-sharepoint-export',
  imports: [
    ConstructionDocumentsWrapperComponent,
    GcTextComponent,
    IpoTranslatePipe,
    GcTableComponent,
    GcButtonComponent,
    AsyncPipe,
    TableRowsSharepointExportPipe,
    OverwriteReportsConfimationModalComponent,
    AsyncPipe,
    TableRowsSharepointExportPipe,
    GcSkeletonComponent,
    GcLoadingSpinnerComponent,
    FeedbackAlertComponent,
    HelpModalComponent,
    ErrorInformationComponent,
  ],
  templateUrl: './sharepoint-export.component.html',
  styleUrl: './sharepoint-export.component.scss',
})
export class SharepointExportComponent
  extends ScreenSizeClassComponent
  implements OnInit, OnDestroy
{
  private readonly sharepointExportService = inject(SharepointExportService);
  private readonly sharepointExportTableService = inject(
    SharepointExportTableService,
  );
  private readonly resourceService = inject(ResourcesService);
  private readonly router = inject(Router);

  sharepointExportResponse$: Observable<SharepointExportResponseQueryLoadingState>;

  cancelSubs$ = new Subject<void>();

  tableColumns = this.sharepointExportTableService.getTableColumns();

  ifsNumber = this.sharepointExportService.ifsNumber;
  previousIfsNumberValue = signal<string>('');
  isLoadingConstructionReports =
    this.sharepointExportService.isLoadingConstructionReports;

  loadingExportReportTypes =
    this.sharepointExportService.loadingExportReportTypes;
  reportTypesSelectedForExport = signal<ReportTypes[]>([]);

  isRefreshingHcConstructionReports =
    this.sharepointExportService.isRefreshingHcConstructionReports;

  isOverwriteReportsModalOpen = signal(false);

  isHelpModalOpen = this.sharepointExportService.isHelpModalOpen;
  userHasPreviouslyUsedSharepointExportKey =
    'userHasPreviouslyUsedSharepointExport';
  userHasPreviouslyUsedSharepointExport = signal(
    localStorage.getItem(this.userHasPreviouslyUsedSharepointExportKey),
  );

  constructor() {
    effect(() => {
      this.handleNewIfs();
    });
    effect(() => {
      if (!this.userHasPreviouslyUsedSharepointExport()) {
        this.openHelpModal();
        localStorage.setItem(
          this.userHasPreviouslyUsedSharepointExportKey,
          'true',
        );
      }
    });
    super();
  }

  ngOnInit(): void {
    this.setLocale();
    this.setIfsNumber();
    this.getData();
  }

  ngOnDestroy(): void {
    this.cancelSubs$.next();
  }

  getData() {
    if (this.ifsNumber().length) {
      this.sharepointExportResponse$ =
        this.sharepointExportService.getConstructionReports$();
    }
  }

  handleOnClickRefreshHcConstructionReports(): void {
    this.sharepointExportService.setIsRefreshingHcConstructionReports(true);
    this.sharepointExportService.setIsLoadingConstructionReports(true);
    this.sharepointExportService.refreshHcConstructionReports$();
  }

  exportReports() {
    this.sharepointExportService.exportReports$(
      this.reportTypesSelectedForExport(),
    );
    this.reportTypesSelectedForExport.set([]);
  }

  handleOnClickExport(
    reportType: ReportTypes,
    data: SharepointExportResponse | undefined,
  ) {
    if (data) {
      this.sharepointExportService.addLoadingExportReportTypes([reportType]);
      this.processExport(data, [reportType]);
    }
  }

  handleOnClickExportAllReady(data: SharepointExportResponse | undefined) {
    if (data) {
      const reportTypes = this.extractReadyReports(data);
      this.sharepointExportService.addLoadingExportReportTypes(reportTypes);
      this.processExport(data, reportTypes);
    }
  }

  handleOnClickRefresh(): void {
    this.getData();
  }

  handleOnClickBack() {
    const currentUrl = this.router.url;
    const newUrl = currentUrl.substring(0, currentUrl.lastIndexOf('/'));
    this.router.navigateByUrl(newUrl);
  }

  handleNewIfs() {
    if (
      this.previousIfsNumberValue() !== '' &&
      this.previousIfsNumberValue() !== this.ifsNumber()
    ) {
      this.getData();
    }
    this.previousIfsNumberValue.set(this.ifsNumber());
  }

  processExport(data: SharepointExportResponse, reportTypes: ReportTypes[]) {
    this.reportTypesSelectedForExport.set(reportTypes);
    if (this.hasExportedReports(data, reportTypes)) {
      this.overwriteReportsModalOpen();
      return;
    }
    this.exportReports();
  }

  hasExportedReports(
    data: SharepointExportResponse,
    reportTypes: ReportTypes[],
  ): boolean {
    return data.some(
      (item) => reportTypes.includes(item.reportType) && item.exported > 0,
    );
  }

  extractReadyReports(data: SharepointExportResponse): ReportTypes[] {
    return data
      .filter(
        (item) =>
          item.status === SharepointExportItemStatus.READY || item.failed > 0,
      )
      .map((item) => item.reportType);
  }

  setIfsNumber(): void {
    this.resourceService
      .getSelectedResource$()
      .pipe(takeUntil(this.cancelSubs$))
      .subscribe((res) => {
        this.sharepointExportService.setIfsNumber(res?.value ?? '');
      });
  }

  setLocale(): void {
    this.sharepointExportService.setLocale(isTopLevelDomainFI() ? 'en' : 'sv');
  }

  //Modal handling

  confirmOverwriteReports() {
    this.sharepointExportService.addLoadingExportReportTypes(
      this.reportTypesSelectedForExport(),
    );
    this.exportReports();
    this.overwriteReportsModalClose();
  }

  cancelOverwriteReports() {
    this.sharepointExportService.removeLoadingExportReportTypes(
      this.reportTypesSelectedForExport(),
    );
    this.reportTypesSelectedForExport.set([]);
    this.overwriteReportsModalClose();
  }

  overwriteReportsModalOpen() {
    this.sharepointExportService.removeLoadingExportReportTypes(
      this.reportTypesSelectedForExport(),
    );
    this.isOverwriteReportsModalOpen.set(true);
  }

  overwriteReportsModalClose() {
    this.isOverwriteReportsModalOpen.set(false);
  }

  openHelpModal() {
    this.sharepointExportService.setIsHelpModalOpen(true);
  }
}
